import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Grid, 
  IconButton, 
  Chip,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Tooltip,
  Avatar
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ptBR from 'date-fns/locale/pt-BR';

import commercialVisitService from '../services/commercialVisit.service';
import { 
  // @ts-ignore - CommercialVisit não existe, usar CommercialVisitType
  CommercialVisitType, 
  COMMERCIAL_VISIT_TYPE_LABELS, 
  COMMERCIAL_VISIT_STATUS_LABELS,
  COMMERCIAL_VISIT_STATUS_COLORS
} from '../types/commercialVisit.types';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import AuthLayout from '../components/AuthLayout';
import LoadingIndicator from '../components/LoadingIndicator';
import ErrorFallback from '../components/ErrorFallback';
import EmptyState from '../components/EmptyState';

const CommercialVisitList: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  
  // Estados para as visitas e paginação
  const [visits, setVisits] = useState<CommercialVisitType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalVisits, setTotalVisits] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // Estados para filtros
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState({
    visitType: '',
    status: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    searchTerm: ''
  });
  
  // Estado para diálogo de exclusão
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [visitIdToDelete, setVisitIdToDelete] = useState<string | null>(null);

  // Carregar visitas
  useEffect(() => {
    fetchVisits();
  }, [page, rowsPerPage, filters]);

  const fetchVisits = async () => {
    setLoading(true);
    setError(null);
    console.log('CommercialVisitList: Iniciando busca de visitas comerciais...');
    
    try {
      // Simplificar a chamada para garantir que estamos obtendo todos os dados
      const response = await commercialVisitService.getCommercialVisits();
      console.log('CommercialVisitList: Resposta completa:', response);
      
      if (response && Array.isArray(response.visits)) {
        console.log(`CommercialVisitList: ${response.visits.length} visitas encontradas`);
        setVisits(response.visits);
        setTotalVisits(response.totalCount || response.visits.length);
      } else {
        // Se a resposta não for no formato esperado, verificar se é um array direto
        if (Array.isArray(response)) {
          console.log(`CommercialVisitList: ${response.length} visitas encontradas (formato alternativo)`);
          setVisits(response);
          setTotalVisits(response.length);
        } else {
          console.warn('CommercialVisitList: Formato de resposta inesperado:', response);
          setVisits([]);
          setTotalVisits(0);
        }
      }
    } catch (error: any) {
      console.error('CommercialVisitList: Erro ao carregar visitas comerciais:', error);
      setError(error.message || 'Erro ao carregar visitas comerciais');
      showSnackbar('Erro ao carregar visitas comerciais. Tente novamente.', 'error');
      setVisits([]);
      setTotalVisits(0);
    } finally {
      setLoading(false);
    }
  };

  // Manipuladores de eventos
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      visitType: '',
      status: '',
      startDate: null,
      endDate: null,
      searchTerm: ''
    });
    setPage(0);
  };

  const handleOpenDeleteDialog = (visitId: string) => {
    setVisitIdToDelete(visitId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setVisitIdToDelete(null);
  };

  const handleDeleteVisit = async () => {
    if (!visitIdToDelete) return;
    
    try {
      await commercialVisitService.deleteCommercialVisit(visitIdToDelete);
      showSnackbar('Visita comercial excluída com sucesso!', 'success');
      fetchVisits();
    } catch (error) {
      console.error('Erro ao excluir visita comercial:', error);
      showSnackbar('Erro ao excluir visita comercial. Tente novamente.', 'error');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // Componentes de renderização
  const renderFilters = () => (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo de Visita</InputLabel>
            <Select
              value={filters.visitType}
              onChange={(e) => handleFilterChange('visitType', e.target.value)}
              label="Tipo de Visita"
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(COMMERCIAL_VISIT_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(COMMERCIAL_VISIT_STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              label="Data Inicial"
              value={filters.startDate}
              onChange={(date) => handleFilterChange('startDate', date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              label="Data Final"
              value={filters.endDate}
              onChange={(date) => handleFilterChange('endDate', date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
        
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Pesquisar"
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton 
                  size="small" 
                  onClick={() => filters.searchTerm ? handleFilterChange('searchTerm', '') : null}
                >
                  {filters.searchTerm ? <ClearIcon /> : <SearchIcon />}
                </IconButton>
              )
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={6}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
            fullWidth
          >
            Limpar Filtros
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderDeleteDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={handleCloseDeleteDialog}
    >
      <DialogTitle>Confirmar Exclusão</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Tem certeza que deseja excluir esta visita comercial? Esta ação não pode ser desfeita.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDeleteDialog} color="primary">
          Cancelar
        </Button>
        <Button onClick={handleDeleteVisit} color="error" autoFocus>
          Excluir
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <AuthLayout title="Gestão Financeira - Visitas Comerciais">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Gestão Financeira - Visitas Comerciais
        </Typography>
        
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/commercial-visits/new')}
          >
            Nova Visita
          </Button>
        </Box>
      </Box>
      
      {showFilters && renderFilters()}
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <LoadingIndicator message="Carregando visitas comerciais..." />
        ) : error ? (
          <ErrorFallback 
            error={error} 
            resetErrorBoundary={fetchVisits} 
            message="Ocorreu um erro ao carregar as visitas comerciais." 
          />
        ) : visits.length === 0 ? (
          <EmptyState 
            title="Nenhuma visita comercial encontrada" 
            // @ts-ignore - Propriedade description não existe no tipo EmptyStateProps
            description="Não há visitas comerciais cadastradas. Clique no botão abaixo para criar uma nova visita."
            icon={<BusinessIcon sx={{ fontSize: 60 }} />}
            buttonText="Nova Visita Comercial"
            onAction={() => navigate('/commercial-visits/new')} 
          />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Data Agendada</TableCell>
                    <TableCell>Responsável</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visits.map((visit) => {
                    console.log('Renderizando visita:', visit);
                    return (
                      <TableRow key={visit._id} hover>
                        <TableCell>
                          {visit.client && typeof visit.client !== 'string' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                sx={{ 
                                  width: 24, 
                                  height: 24, 
                                  bgcolor: 'primary.main',
                                  mr: 1,
                                  fontSize: '0.8rem'
                                }}
                              >
                                {visit.client.name ? visit.client.name.charAt(0) : '?'}
                              </Avatar>
                              <Typography variant="body2">{visit.client.name || 'Cliente'}</Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2">Cliente não especificado</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Usar o campo 'type' em vez de 'visitType' */}
                          {visit.type && COMMERCIAL_VISIT_TYPE_LABELS[visit.type] || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {visit.status ? (
                            <Chip 
                              label={COMMERCIAL_VISIT_STATUS_LABELS[visit.status] || visit.status} 
                              size="small"
                              // @ts-ignore - Tipo string não é atribuível ao tipo esperado
                              color={COMMERCIAL_VISIT_STATUS_COLORS[visit.status] || 'default'}
                            />
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {visit.scheduledDate ? new Date(visit.scheduledDate).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {/* Usar o campo 'assignedTo' se estiver populado, ou 'createdBy' como fallback */}
                          {(visit.assignedTo && typeof visit.assignedTo !== 'string') ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                sx={{ 
                                  width: 24, 
                                  height: 24, 
                                  bgcolor: 'secondary.main',
                                  mr: 1,
                                  fontSize: '0.8rem'
                                }}
                              >
                                {visit.assignedTo.displayName ? visit.assignedTo.displayName.charAt(0) : '?'}
                              </Avatar>
                              <Typography variant="body2">{visit.assignedTo.displayName || 'Usuário'}</Typography>
                            </Box>
                          ) : (visit.createdBy && typeof visit.createdBy !== 'string') ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                sx={{ 
                                  width: 24, 
                                  height: 24, 
                                  bgcolor: 'secondary.main',
                                  mr: 1,
                                  fontSize: '0.8rem'
                                }}
                              >
                                {visit.createdBy.displayName ? visit.createdBy.displayName.charAt(0) : '?'}
                              </Avatar>
                              <Typography variant="body2">{visit.createdBy.displayName || 'Usuário'}</Typography>
                            </Box>
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Visualizar">
                            <IconButton 
                              size="small"
                              onClick={() => navigate(`/commercial-visits/${visit._id}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton 
                              size="small"
                              onClick={() => navigate(`/commercial-visits/${visit._id}/edit`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Custos">
                            <IconButton 
                              size="small"
                              onClick={() => navigate(`/costs?commercialVisit=${visit._id}`)}
                              color="primary"
                            >
                              <AssignmentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton 
                              size="small"
                              onClick={() => handleOpenDeleteDialog(visit._id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalVisits}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>
      
      {renderDeleteDialog()}
    </AuthLayout>
  );
};

export default CommercialVisitList;
